import {
  Alert,
  Box,
  Button,
  Collapse,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import TuneIcon from "@mui/icons-material/Tune";
import { useState } from "react";
import EnterKeyListener from "./EnterKeyListener";

// Each basic field maps one form input to one backend query param. A field
// listing `exclusiveWith` clears those other field(s) when it gets a value.
// This is deliberately asymmetric: entering an exact year clears that range's
// start/end (since a range is meaningless once an exact year is picked), but
// entering a start or end clears only the exact year, not each other, since
// both together are what make up the range.
const BASIC_FIELDS = [
  {
    id: "year",
    label: "Vuosi",
    param: "vuosi",
    exclusiveWith: ["yearStart", "yearEnd"],
  },
  {
    id: "yearStart",
    label: "Aloitusvuosi",
    param: "yearStart",
    exclusiveWith: ["year"],
  },
  {
    id: "yearEnd",
    label: "Lopetusvuosi",
    param: "yearEnd",
    exclusiveWith: ["year"],
  },
  { id: "lastName", label: "Sukunimi", param: "sukunimi" },
  { id: "firstName", label: "Etunimi", param: "etunimi" },
  { id: "patronym", label: "Patronyymi", param: "patronyymi" },
  {
    id: "birthYear",
    label: "Syntymävuosi",
    param: "syntymävuosi",
    exclusiveWith: ["birthYearStart", "birthYearEnd"],
  },
  {
    id: "birthYearStart",
    label: "Aloitusvuosi",
    param: "birthYearStart",
    exclusiveWith: ["birthYear"],
  },
  {
    id: "birthYearEnd",
    label: "Lopetusvuosi",
    param: "birthYearEnd",
    exclusiveWith: ["birthYear"],
  },
  { id: "county", label: "Paikkakunta", param: "paikkakunta" },
];

// Only shown once "Lisää hakuehtoja" is toggled on.
const EXTENDED_FIELDS = [
  { id: "district", label: "Kaupunginosa", param: "kaupunginosa" },
  { id: "village", label: "Kylä tai kortteli", param: "kylä" },
  { id: "estate", label: "Tila tai katu", param: "tila" },
  { id: "profession", label: "Ammatti tai arvo", param: "ammatti tai arvo" },
];

const ALL_FIELDS = [...BASIC_FIELDS, ...EXTENDED_FIELDS];

const INITIAL_VALUES = Object.fromEntries(
  ALL_FIELDS.map((field) => [field.id, ""]),
);

function YearField({ field, values, onChange }) {
  return (
    <TextField
      id={field.id}
      label={field.label}
      onChange={(e) => onChange(field.id, e.target.value)}
      value={values[field.id]}
      type="number"
      InputProps={{ inputProps: { min: 0 } }}
    />
  );
}

function YearRangeGroup({ title, exact, start, end, values, onChange }) {
  return (
    <Box>
      <SectionTitle>{title}</SectionTitle>
      <Stack spacing={1}>
        <YearField field={exact} values={values} onChange={onChange} />
        <Typography variant="caption" color="text.secondary">
          tai aikaväli
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <YearField field={start} values={values} onChange={onChange} />
          <Typography color="text.secondary">–</Typography>
          <YearField field={end} values={values} onChange={onChange} />
        </Stack>
      </Stack>
    </Box>
  );
}

function SectionTitle({ children }) {
  return (
    <Typography
      variant="overline"
      component="h3"
      color="primary"
      sx={{ display: "block", mb: 1, lineHeight: 1.6 }}
    >
      {children}
    </Typography>
  );
}

function TextFieldFor({ field, values, onChange }) {
  return (
    <TextField
      id={field.id}
      label={field.label}
      onChange={(e) => onChange(field.id, e.target.value)}
      value={values[field.id]}
    />
  );
}

const GRID_SX = {
  display: "grid",
  gap: 1.5,
  gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
};

function fieldById(id) {
  return ALL_FIELDS.find((field) => field.id === id);
}

function Search(props) {
  const [values, setValues] = useState(INITIAL_VALUES);
  const [fullSearch, setFullSearch] = useState(false);
  const [emptySearch, setEmptySearch] = useState(false);
  const [failedConnection, setFailedConnection] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleEnter = () => {
    doSearch();
  };

  const toggleSearchType = () => {
    setFullSearch(!fullSearch);
  };

  const handleFieldChange = (id, value) => {
    setValues((prev) => {
      const next = { ...prev, [id]: value };
      if (value) {
        for (const exclusiveId of fieldById(id).exclusiveWith ?? []) {
          next[exclusiveId] = "";
        }
      }
      return next;
    });
  };

  const createSearchParams = () => {
    const fields = fullSearch ? ALL_FIELDS : BASIC_FIELDS;
    const params = new URLSearchParams();
    for (const field of fields) {
      const value = values[field.id].trim().toLowerCase();
      if (value) {
        params.append(field.param, value);
      }
    }
    return params.toString() ? `?${params.toString()}` : "";
  };

  const doSearch = async () => {
    setLoading(true);
    const parameters = createSearchParams();
    if (parameters) {
      setEmptySearch(false);
      setFailedConnection(false);
      try {
        await props.handleSearch(parameters);
        window.setTimeout(scrollDown, 100);
      } catch (error) {
        setFailedConnection(true);
      }
    } else {
      setEmptySearch(true);
    }
    setLoading(false);
  };

  const scrollDown = () => {
    const searchResultsHeader = document.getElementById("searchResultsHeader");
    searchResultsHeader?.scrollIntoView({ behavior: "smooth" });
  };

  const startOver = () => {
    setValues(INITIAL_VALUES);
    props.handleEmpty();
  };

  const fieldProps = { values, onChange: handleFieldChange };

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, overflow: "hidden" }}>
      <EnterKeyListener onEnter={handleEnter} />
      {emptySearch && (
        <Alert sx={{ mb: 2 }} severity="info">
          Syötä ainakin yksi hakutermi.
        </Alert>
      )}
      {failedConnection && !emptySearch && (
        <Alert sx={{ mb: 2 }} severity="error">
          Tietokantaan ei juuri nyt saatu yhteyttä. Yritä uudelleen hetken
          kuluttua.
        </Alert>
      )}
      <Stack spacing={3}>
        <Box>
          <SectionTitle>Henkilö</SectionTitle>
          <Box sx={GRID_SX}>
            <TextFieldFor field={fieldById("lastName")} {...fieldProps} />
            <TextFieldFor field={fieldById("firstName")} {...fieldProps} />
            <TextFieldFor field={fieldById("patronym")} {...fieldProps} />
            {fullSearch && (
              <TextFieldFor field={fieldById("profession")} {...fieldProps} />
            )}
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 1 }}
          >
            Vinkki: tähtimerkki (*) hakutermin alussa tai lopussa korvaa
            kirjaimia, esim. <i>Huovi*</i> löytää sekä Huovilan että Huovisen.
          </Typography>
        </Box>

        <Box
          sx={{
            display: "grid",
            gap: 3,
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          }}
        >
          <YearRangeGroup
            title="Henkikirjan vuosi"
            exact={fieldById("year")}
            start={fieldById("yearStart")}
            end={fieldById("yearEnd")}
            {...fieldProps}
          />
          <YearRangeGroup
            title="Syntymävuosi"
            exact={fieldById("birthYear")}
            start={fieldById("birthYearStart")}
            end={fieldById("birthYearEnd")}
            {...fieldProps}
          />
        </Box>

        <Box>
          <SectionTitle>Paikka</SectionTitle>
          <Box sx={GRID_SX}>
            <TextFieldFor field={fieldById("county")} {...fieldProps} />
            {fullSearch &&
              ["district", "village", "estate"].map((id) => (
                <TextFieldFor key={id} field={fieldById(id)} {...fieldProps} />
              ))}
          </Box>
        </Box>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "stretch", sm: "center" }}
          sx={{ pt: 2, borderTop: 1, borderColor: "divider" }}
        >
          <Button
            id="searchButton"
            variant="contained"
            size="large"
            startIcon={<SearchIcon />}
            onClick={() => doSearch()}
            sx={{ px: 4 }}
          >
            Hae
          </Button>
          <Button color="inherit" size="large" onClick={() => startOver()}>
            Tyhjennä
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button startIcon={<TuneIcon />} onClick={() => toggleSearchType()}>
            {fullSearch ? "Vähemmän hakuehtoja" : "Lisää hakuehtoja"}
          </Button>
        </Stack>
      </Stack>
      <Collapse in={loading && !emptySearch}>
        <LinearProgress sx={{ mt: 2 }} />
      </Collapse>
    </Paper>
  );
}

export default Search;
